import { fetchWithAuth, getAccessToken } from '@/lib/authenticated-fetch';
import { extractAddressText } from '@/lib/json-fields';
import { getSharedSessionId } from '@/lib/session-id';

export interface PaymentInfo {
  qrCode: string;
  qrCodeUrl: string;
  bankUrls: Array<{
    name: string;
    description: string;
    logo: string;
    link: string;
  }>;
  invoiceId: string;
}

export type ShippingInfo = {
  fullName: string;
  phone: string;
  address: string;
  city: string;
  zipCode: string;
};

export function getInitialShippingInfo(): ShippingInfo {
  const saved = sessionStorage.getItem('checkout-shipping-info');
  if (saved) {
    try {
      return JSON.parse(saved) as ShippingInfo;
    } catch {
      return { fullName: '', phone: '', address: '', city: '', zipCode: '' };
    }
  }

  return { fullName: '', phone: '', address: '', city: '', zipCode: '' };
}

export const normalizeQrCodeSrc = (raw: string): string => {
  const value = (raw || '').trim();
  if (!value) return '';
  if (/^data:image\//i.test(value)) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `data:image/png;base64,${value}`;
};

export const normalizeBankLogoUrl = (raw: string): string => {
  const value = (raw || '').trim();
  if (!value) return '';
  if (value.startsWith('//')) return `https:${value}`;
  if (/^http:\/\//i.test(value)) return value.replace(/^http:\/\//i, 'https://');
  if (/^https?:\/\//i.test(value)) return value;
  return '';
};

export async function fetchSavedProfileAddress() {
  const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/profile`);

  if (!response.ok) {
    throw new Error('Failed to fetch profile');
  }

  const data = await response.json();
  if (!data.profile?.address || !data.profile?.phone || !data.profile?.name) {
    return null;
  }

  return {
    fullName: data.profile.name,
    phone: data.profile.phone,
    address: extractAddressText(data.profile.address),
    city: '',
    zipCode: '',
  } satisfies ShippingInfo;
}

export async function createCheckoutOrder(input: {
  items: Array<{
    variantId: string;
    productName: string;
    variantName: string;
    variantPrice: number;
    quantity: number;
    imagePath: string;
  }>;
  shippingAddress: ShippingInfo;
  total: number;
}) {
  const token = await getAccessToken();
  if (!token) {
    return { needsLogin: true as const };
  }

  const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...input,
      chatSessionId: getSharedSessionId(),
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorData = data as { error?: string; details?: Array<{ message?: string; field?: string }> | string };
    if (errorData.error === 'Validation failed' && Array.isArray(errorData.details)) {
      const validationErrors = errorData.details
        .map((err) => err.message || `${err.field}: validation error`)
        .join(', ');
      throw new Error(validationErrors);
    }

    throw new Error((errorData as { details?: string; error?: string }).details || errorData.error || 'Failed to create order');
  }

  return {
    needsLogin: false as const,
    order: data.order as { id: string },
    payment: data.payment as PaymentInfo,
  };
}

export async function fetchOrderPaymentStatus(orderId: string) {
  const token = await getAccessToken();
  if (!token) {
    return { needsLogin: true as const, paid: false };
  }

  const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/orders/${orderId}/payment-status`);

  if (!response.ok) {
    throw new Error('Payment status check failed');
  }

  const data = await response.json();
  return {
    needsLogin: false as const,
    paid: Boolean(data.paid),
  };
}
